import React, { useState } from 'react';
import { MapPin, Calendar, Maximize2 } from 'lucide-react';
import { projects } from '../data';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumb } from '../components/Breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ProjectModal } from '../components/ProjectModal';
import SEO from '../components/SEO';
import { getThumbnail } from '../lib/imageOptimization';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [statusTab, setStatusTab] = useState('completed');

  const thumb = (url) => getThumbnail(url, 400);

  const categories = ['all', ...new Set(projects.map(p => p.category))];

  const completedProjects = projects.filter(p => p.status === 'completed');
  const ongoingProjects = projects.filter(p => p.status === 'ongoing');
  const upcomingProjects = projects.filter(p => p.status === 'upcoming');

  const getProjectsByStatus = () => {
    const statusProjects = statusTab === 'completed' ? completedProjects : statusTab === 'ongoing' ? ongoingProjects : upcomingProjects;
    return filter === 'all' 
      ? statusProjects 
      : statusProjects.filter(p => p.category === filter);
  };

  const filteredProjects = getProjectsByStatus();

  return (
    <div>
      <SEO
        title="Our Projects"
        description="Browse the portfolio of Aashi Constructions - completed residential and commercial construction projects in Bangalore showcasing quality craftsmanship."
        keywords="construction projects bangalore, completed buildings, portfolio, residential projects, commercial projects"
        url="/projects"
      />
      <Breadcrumb items={[{ label: 'Projects' }]} />
      
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src={`${process.env.REACT_APP_CLOUDINARY_BASE}/abhishek_eswar.jpg`}
          alt="Projects"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Our Portfolio
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Explore our portfolio of quality construction projects, each a testament to our commitment to excellence and craftsmanship
          </p>
        </div>
      </section>

      {/* Status Tabs */}
      <section className="py-6 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={statusTab} onValueChange={setStatusTab} className="w-full">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3">
              <TabsTrigger value="completed" className="text-sm sm:text-base">
                Completed
              </TabsTrigger>
              <TabsTrigger value="ongoing" className="text-sm sm:text-base">
                Ongoing
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="text-sm sm:text-base">
                Upcoming
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? 'default' : 'outline'}
                size="sm"
                className={`capitalize transition-all duration-300 text-sm md:text-base ${
                  filter === category
                    ? 'bg-brand-600 hover:bg-brand-700 text-white'
                    : 'border-gray-300 hover:border-brand-600 hover:text-brand-600'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-lg md:text-xl text-gray-600">No projects found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="group cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="relative overflow-hidden rounded-xl md:rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                    <img
                      src={thumb(project.image)}
                      alt={project.title}
                      loading="lazy"
                      className="w-full aspect-[16/9] object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
                    
                    <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-end">
                      <Badge className="w-fit mb-2 sm:mb-3 bg-brand-600 hover:bg-brand-700 text-white border-0 text-xs sm:text-sm">
                        {project.category}
                      </Badge>
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-brand-400 transition-colors duration-300">
                        {project.title}
                      </h3>
                      <div className="flex items-center text-white/90 text-sm mb-2">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="text-xs sm:text-sm">{project.location}</span>
                      </div>
                      <div className="flex items-center justify-between text-white/80 text-xs sm:text-sm">
                        <span className="flex items-center">
                          <Maximize2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {project.area}
                        </span>
                        {/* <span className="flex items-center">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {project.year}
                        </span> */}
                      </div>
                    </div>

                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />}
    </div>
  );
};

export default Projects;
